import React, { useEffect, useState, useRef, ComponentType } from "react";

import { Card, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import styled, { useTheme } from "styled-components";

import useDecodedParams from "Hooks/useDecodedParams";
import client, { request } from "Libs/platform";
import { PluginManifest } from "src/root/pages/pluginApps/plugin.types";

import * as ds from "./designSystemExports";
import { ErrorBoundary } from "./ErrorBoundary";

type ContextProps = {
  manifest: PluginManifest;
  entrypoint: string;
};

type actionsSetType = "PLUGINS_RESIZE_IFRAME";
type actionsReqType = "PLUGINS_PROPS_REQ";
type actionsResType = "PLUGINS_PROPS_RES";

function loadApp(app: string) {
  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  return Function(`return function(define) { return ${app} }`)();
}

function Context({ manifest, entrypoint, ...props }: ContextProps) {
  const [Component, setComponent] = useState<ComponentType<any>>();
  const [error, setError] = useState<null | Error>(null);

  const intl = useIntl();
  const theme = useTheme();
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!manifest.url || manifest.type === "iframe") return;

    function define(_, _2, appFunc) {
      const exports = appFunc(React, styled);

      setComponent(() => exports[entrypoint || "Main"]);
    }

    async function load() {
      try {
        const scriptResp = await fetch(manifest.url);
        const script = await scriptResp.text();
        const application = loadApp(script);
        application(define);
      } catch (err) {
        if (err instanceof Error) {
          setError(err);
        }
      }
    }

    load();
  }, [manifest, entrypoint]);

  const postMessageToIframe = (action: actionsResType, data: any) => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    iframe.contentWindow?.postMessage({ action, data }, "*");
  };

  const resizeIframe = (height: number) => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    iframe.style.height = `${height}px`;
  };

  const getProps = (reqId: number) => {
    postMessageToIframe("PLUGINS_PROPS_RES", {
      reqId,
      theme: theme?.name,
      projectId,
      environmentId,
      organizationId
    });
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resizeObserver = new ResizeObserver(entries => {
      resizeIframe(entries[0].contentRect.height);
    });

    iframe.contentWindow?.document.body &&
      resizeObserver.observe(iframe.contentWindow.document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.endsWith("plugins.pltfrm.sh")) return;

      const {
        action,
        data
      }: { action: actionsSetType | actionsReqType; data: any } =
        event.data || {};
      if (!action) return;

      switch (action) {
        case "PLUGINS_RESIZE_IFRAME":
          resizeIframe(data);
          break;
        case "PLUGINS_PROPS_REQ":
          getProps(data.reqId);
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [iframeRef]);

  if (error) {
    if (error.message.toLowerCase().includes("failed to fetch"))
      return (
        <Card padding="32px">
          <Typography.Title tag="h3">
            {intl.formatMessage(
              { id: "error.plugin.error_loading" },
              { name: manifest.name }
            )}
          </Typography.Title>
          <Typography.BodyText>
            {intl.formatMessage({ id: "error.plugin.unreachable" })}
          </Typography.BodyText>
        </Card>
      );

    return <div>Error: {error.message}</div>;
  }

  return (
    <ErrorBoundary key={manifest.id}>
      {manifest?.type === "iframe" ? (
        <iframe
          ref={iframeRef}
          src={manifest.url}
          title={manifest.id || "iframe"}
          style={{ width: "100%", height: "80vh", border: "none" }}
          allow="fullscreen"
        />
      ) : (
        <Card>
          {Component && (
            <Component
              client={client}
              request={request}
              theme={theme}
              ds={ds}
              {...props}
            />
          )}
        </Card>
      )}
    </ErrorBoundary>
  );
}

export default React.memo(Context);
